import { useEffect, useRef, useState } from 'react';
import admissionsHero1 from './assets/news/news1.jpg';
import admissionsHero2 from './assets/news/news5.jpg';
import admissionsHero3 from './assets/news/news8.jpg';
import admissionsDocImage from './assets/news/news4.jpg';

function AdmissionsPage({ page }) {
  const heroSlides = page?.hero_slides || [admissionsHero1, admissionsHero2, admissionsHero3];
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  useEffect(() => {
    const animatedItems = document.querySelectorAll('.adm-animate');
    if (!animatedItems.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    animatedItems.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  const heroTitle = page?.title || 'Admissions';
  const heroSummary =
    page?.summary ||
    'Begin your admissions journey with clear steps, supportive guidance, and a welcoming school community.';

  const admissionsLevels = page?.admissions_levels || [
    {
      title: 'Preschool & Kindergarten Admissions',
      note: 'Trial class available',
      description:
        'A gentle entry pathway focused on readiness, social-emotional growth, and a positive first school experience.',
      image: admissionsHero1,
      ageRange: 'Ages 3-6',
      intake: 'Rolling intake with priority Jan - Jul',
      assessment: 'Play-based observation, readiness milestones, and family conversation.',
      support: 'Parent orientation, gentle transition plan, and teacher check-ins in the first term.',
      highlights: [
        'Readiness observation and play-based assessment',
        'Parent-child interaction session',
        'Trial class to experience our learning environment',
        'Flexible intake throughout the year',
      ],
      process: 'Inquiry → Trial Class → Observation → Acceptance',
      outcomes: [
        'Comfortable classroom transition with strong social-emotional foundation',
        'Clear readiness feedback for families before school begins',
        'Personalized onboarding for early learners',
      ],
      timeline: '2-3 weeks',
    },
    {
      title: 'Primary Admissions',
      note: 'Grades 1-6',
      description:
        'Placement based on age, readiness, and academic fit with supportive guidance for families new to the school.',
      image: admissionsHero2,
      ageRange: 'Ages 7-12',
      intake: 'Main intake Jan - Jun, limited mid-year slots',
      assessment: 'English, Math, reasoning checks with teacher observation.',
      support: 'Placement guidance, learning pathway briefing, and progress roadmap.',
      highlights: [
        'Academic assessment in English, Math, and reasoning',
        'Teacher observation and learning style evaluation',
        'Student interview to understand interests and goals',
        'Placement in appropriate grade level',
      ],
      process: 'Inquiry → Assessment → Interview → Placement → Acceptance',
      outcomes: [
        'Grade placement aligned to readiness and learning style',
        'Clear academic expectations and support plan for the term',
        'Smooth onboarding for students transferring schools',
      ],
      timeline: '3-4 weeks',
    },
    {
      title: 'Secondary Admissions',
      note: 'Grades 7-12',
      description:
        'Academic review and student interview to ensure alignment with learning goals, leadership, and character development.',
      image: admissionsHero3,
      ageRange: 'Ages 13-18',
      intake: 'Main intake Jan - May, limited seats by track',
      assessment: 'Academic diagnostics, goal interview, and transcript review.',
      support: 'Mentoring alignment, subject mapping, and leadership pathway briefing.',
      highlights: [
        'Comprehensive academic assessment and aptitude testing',
        'In-depth student interview on aspirations and values',
        'Review of academic transcript and recommendations',
        'Leadership and character fit evaluation',
      ],
      process: 'Inquiry → Assessment → Interview → Review → Acceptance',
      outcomes: [
        'Learning plan aligned with subject strength and university goals',
        'Leadership pathway recommendations based on interests',
        'Clear expectations for academic milestones',
      ],
      timeline: '4-5 weeks',
    },
  ];

  const keyDates = page?.key_dates || [
    {
      title: 'Applications Open',
      tag: 'Jan',
      date: 'Jan 15, 2026',
      description: 'Online applications open for the new intake year. Create an account and start your PPDB form.',
    },
    {
      title: 'Early Bird Window',
      tag: 'Feb',
      date: 'Jan 15 - Feb 28, 2026',
      description: 'Priority scheduling for campus visits and assessment slots, plus faster feedback from admissions.',
    },
    {
      title: 'Assessment Week',
      tag: 'May',
      date: 'May 10 - May 24, 2026',
      description: 'Placement tests, classroom observations, or interviews based on level and student readiness.',
    },
    {
      title: 'Offer Release',
      tag: 'Jun',
      date: 'Jun 1 - Jun 7, 2026',
      description: 'Admission results, payment instructions, and enrollment steps sent to families.',
    },
    {
      title: 'Orientation Week',
      tag: 'Jul',
      date: 'Jul 6 - Jul 10, 2026',
      description: 'Family briefings, classroom introductions, and readiness activities to build confidence.',
    },
    {
      title: 'School Opening Day',
      tag: 'Jul',
      date: 'Jul 13, 2026',
      description: 'Welcome day for students and families as the new academic year begins.',
    },
  ];

  const feesPayment = page?.fees_payment || [
    {
      title: 'Registration & Admission Fees',
      icon: 'fa-clipboard-list',
      summary: 'Clear onboarding fees so families know what to expect before enrollment.',
      description: 'One-time fees charged upon admission acceptance to secure your child\'s placement and complete enrollment.',
      items: [
        'Application and assessment fee (non-refundable)',
        'One-time registration fee upon acceptance',
        'Uniform and student kit (if applicable)',
        'Enrollment confirmation and placement security',
        'Access to student portal and parent dashboard',
      ],
      highlight: 'Transparent pricing with no hidden charges',
    },
    {
      title: 'Tuition & Payment Plans',
      icon: 'fa-credit-card',
      summary: 'Flexible payment options built around family needs and level pathways.',
      description: 'Annual tuition with multiple payment arrangements to support your family\'s financial planning.',
      items: [
        'Annual tuition based on level (Preschool, Primary, Secondary)',
        'Flexible installment options: monthly, per term, or semester',
        'Sibling discount or multi-child package (where applicable)',
        'Payment methods: bank transfer, virtual account, or on-site cashier',
        'Automatic payment reminders and online payment tracking',
        'Receipts and detailed billing via admissions portal',
      ],
      highlight: 'Installment plans available to ease financial burden',
    },
    {
      title: 'Scholarships & Financial Support',
      icon: 'fa-medal',
      summary: 'Limited assistance for qualifying students with transparent review steps.',
      description: 'Merit-based and needs-based scholarships available for eligible families. Limited seats with rolling review.',
      items: [
        'Merit-based scholarships for academic excellence',
        'Needs-based financial assistance (subject to availability)',
        'Transparent documentation and review process',
        'Application deadline and eligibility criteria provided',
        'Scholarship review conducted by admissions committee',
        'Contact admissions team for personalized guidance',
      ],
      highlight: 'Supporting talented students from all backgrounds',
    },
    {
      title: 'Additional Services & Programs',
      icon: 'fa-star',
      summary: 'Optional services that enhance your child\'s school experience.',
      description: 'Enrichment programs and support services available to complement your child\'s learning journey.',
      items: [
        'Transportation and shuttle service (optional, monthly fee)',
        'Lunch program with nutritious meal plans (optional)',
        'After-school care and enrichment clubs',
        'School trips, excursions, and special events',
        'Extended learning programs and tutoring support',
        'Technology and digital learning resources',
      ],
      highlight: 'Flexible add-ons to support your child\'s growth',
    },
  ];

  const onlineApplication = page?.online_application || {
    title: 'Online Application Form',
    summary:
      'Ready to enroll? Start your application online in just a few minutes — our admissions team will guide you at every step.',
    ctaLabel: 'Apply Online Now',
    ctaHref: '#register',
    note: 'Have questions? Contact admissions anytime. Already have an account? Log in to continue your application and upload documents.',
    image: admissionsDocImage,
    highlights: ['10-minute application', 'Secure document upload', 'Real-time status tracking'],
  };

  return (
    <section className="admissions-page-shell">
      <section className="admissions-hero">
        <div className="admissions-hero-media" aria-hidden="true">
          {heroSlides.map((slide, index) => (
            <div
              key={slide}
              className={`admissions-hero-slide ${index === activeSlide ? 'active' : ''}`}
            >
              <img src={slide} alt="Admissions hero" />
            </div>
          ))}
        </div>
        <div className="admissions-hero-overlay" aria-hidden="true" />
        <div className="admissions-hero-content">
          <p className="admissions-hero-kicker">Admissions</p>
          <h2>{heroTitle}</h2>
          <p>{heroSummary}</p>
          <div className="admissions-hero-dots" role="tablist" aria-label="Admissions hero slides">
            {heroSlides.map((slide, index) => (
              <button
                key={slide}
                type="button"
                className={`admissions-hero-dot ${index === activeSlide ? 'active' : ''}`}
                onClick={() => setActiveSlide(index)}
                aria-label={`Show slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="admissions-levels">
        <div className="admissions-levels-header">
          <p className="admissions-levels-kicker">Admissions Pathways</p>
          <h3>Tailored Admissions for Every Stage</h3>
          <p>
            Discover admissions programs designed for Preschool through Secondary, each with clear processes, transparent timelines, and supportive guidance for families.
          </p>
        </div>

        <AdmissionsLevelsCarousel levels={admissionsLevels} />
      </section>

      <section className="admissions-process admissions-process-steps" id="admissions-process">
        <div className="admissions-process-header">
          <p className="admissions-process-kicker">Admissions Process</p>
          <h3>Your child’s journey starts here, with guidance at every step</h3>
          <p>
            Discover each milestone from inquiry to opportunity. Hover or swipe the cards to see what happens next and
            how we support your family along the way.
          </p>
        </div>

        <AdmissionsProcessSteps />
      </section>



      <section className="admissions-requirements" id="admissions-requirements">
        <div className="admissions-requirements-header">
          <p className="admissions-requirements-kicker">Requirements</p>
          <h3>Documents & Eligibility by Level</h3>
          <p>Clear requirements for each admission level to help families prepare their applications with confidence.</p>
        </div>

        <AdmissionsRequirementsSection />
      </section>

      <AdmissionsKeyDatesTimeline keyDates={keyDates} />

      <section className="fees-payment" id="fees-payment">
        <div className="fees-payment-header">
          <p className="fees-payment-kicker">Fees & Payment Information</p>
          <h3>Transparent Fees, Flexible Plans & Clear Payment Options</h3>
          <p>
            A comprehensive overview of admission fees, tuition, and payment options for each level. We believe in transparent pricing with no hidden charges, and we offer flexible payment arrangements to support your family's financial planning.
          </p>
        </div>

        <div className="fees-payment-grid">
          {feesPayment.map((block) => (
            <article className="fees-payment-card adm-animate" key={block.title}>
              <div className="fees-payment-card-header">
                <div className="fees-payment-icon">
                  <i className={`fas ${block.icon}`} />
                </div>
                <h4>{block.title}</h4>
              </div>
              <p className="fees-payment-summary">{block.summary}</p>
              <p className="fees-payment-description">{block.description}</p>
              <ul className="fees-payment-list">
                {block.items.map((item) => (
                  <li key={item}>
                    <span className="list-icon">
                      <i className="fas fa-check" />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="fees-payment-highlight">
                <span className="highlight-icon">
                  <i className="fas fa-lightbulb" />
                </span>
                <p>{block.highlight}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="online-application" id="online-application">
        <div className="online-application-header">
          <p className="online-application-kicker">Online Application</p>
          <h2>{onlineApplication.title}</h2>
          <p>{onlineApplication.summary}</p>
        </div>

        <div className="online-application-card adm-animate">
          <div className="online-application-content">
            <h3>Apply Faster, Track Every Step</h3>
            <p className="online-application-note">{onlineApplication.note}</p>
            <div className="online-application-highlights">
              {onlineApplication.highlights.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
            <div className="online-application-actions">
              <a className="online-application-cta" href={onlineApplication.ctaHref}>
                {onlineApplication.ctaLabel}
              </a>
            </div>
          </div>
          <div className="online-application-media" aria-hidden="true">
            <img src={onlineApplication.image} alt="Online application" />
            <div className="online-application-badge">
              <strong>Start Online</strong>
              <span>Submit, schedule, visit campus</span>
            </div>
          </div>
        </div>
      </section>
    </section>
  );
}


function AdmissionsLevelsCarousel({ levels }) {
  const trackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handlePrev = () => {
    const newIndex = activeIndex === 0 ? levels.length - 1 : activeIndex - 1;
    setActiveIndex(newIndex);
    scrollToCard(newIndex);
  };

  const handleNext = () => {
    const newIndex = activeIndex === levels.length - 1 ? 0 : activeIndex + 1;
    setActiveIndex(newIndex);
    scrollToCard(newIndex);
  };

  const scrollToCard = (index) => {
    const track = trackRef.current;
    if (!track) return;
    
    const card = track.children[index];
    if (card) {
      // Calculate scroll position to center the card
      const cardWidth = card.offsetWidth;
      const trackWidth = track.offsetWidth;
      const cardLeft = card.offsetLeft;
      
      // Center position = card position - (viewport width / 2) + (card width / 2)
      const scrollPosition = cardLeft - (trackWidth / 2) + (cardWidth / 2);
      
      track.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="admissions-levels-carousel-shell">
      <button
        className="admissions-levels-carousel-nav left"
        onClick={handlePrev}
        aria-label="Previous admissions level"
      >
        ←
      </button>

      <div className="admissions-levels-carousel-viewport">
        <div className="admissions-levels-carousel-track" ref={trackRef}>
          {levels.map((level, index) => (
            <article
              key={level.title}
              className={`admissions-level-card adm-animate ${index === activeIndex ? 'active' : ''}`}
              onClick={() => {
                setActiveIndex(index);
                scrollToCard(index);
              }}
            >
              <div className="admissions-level-header-section">
                <div className="admissions-level-top">
                  <h4>{level.title}</h4>
                  <span className="admissions-level-badge">{level.note}</span>
                </div>
                <p className="admissions-level-age">{level.ageRange}</p>
              </div>

              <div className="admissions-level-media">
                <img src={level.image} alt={level.title} />
              </div>

              <div className="admissions-level-body">
                <p className="admissions-level-description">{level.description}</p>

                <div className="admissions-level-focus">
                  <div className="admissions-level-focus-card">
                    <span>Assessment</span>
                    <p>{level.assessment}</p>
                  </div>
                  <div className="admissions-level-focus-card">
                    <span>Family Support</span>
                    <p>{level.support}</p>
                  </div>
                </div>
                
                <div className="admissions-level-highlights">
                  <h5>Key Checkpoints</h5>
                  <ul>
                    {level.highlights.map((highlight) => (
                      <li key={highlight}>{highlight}</li>
                    ))}
                  </ul>
                </div>

                <div className="admissions-level-pathway">
                  <h5>Pathway</h5>
                  <div className="admissions-level-steps">
                    {level.process.split('→').map((step) => (
                      <span key={`${level.title}-${step}`}>{step.trim()}</span>
                    ))}
                  </div>
                </div>

                <div className="admissions-level-outcomes">
                  <h5>Expected Outcomes</h5>
                  <ul>
                    {level.outcomes.map((outcome) => (
                      <li key={outcome}>{outcome}</li>
                    ))}
                  </ul>
                </div>

                <div className="admissions-level-timeline">
                  <div className="timeline-item">
                    <span className="timeline-label">Intake Window</span>
                    <p>{level.intake}</p>
                  </div>
                  <div className="timeline-item">
                    <span className="timeline-label">Estimated Timeline</span>
                    <p>{level.timeline}</p>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      <button
        className="admissions-levels-carousel-nav right"
        onClick={handleNext}
        aria-label="Next admissions level"
      >
        →
      </button>
    </div>
  );
}

function AdmissionsProcessSteps() {
  const steps = [
    {
      title: 'Inquiry',
      description: 'Contact our admissions team to get program details, fees, and available intake slots.',
      image: admissionsHero2,
    },
    {
      title: 'Visit',
      description: 'Schedule a campus tour or information session to meet the team and explore facilities.',
      image: admissionsHero1,
    },
    {
      title: 'Test/Interview',
      description: 'Student observation, assessment, or interview to understand readiness and fit.',
      image: admissionsHero3,
    },
    {
      title: 'Acceptance',
      description: 'Receive admission results and complete registration to secure your child’s place.',
      image: admissionsHero2,
    },
    {
      title: 'Scholarship',
      description: 'Qualifying students may apply for scholarships or financial support. Limited seats, transparent review.',
      image: admissionsDocImage,
    },
    {
      title: 'Opportunity',
      description: 'Unlock student opportunities through programs, enrichment, and a supportive school community.',
      image: admissionsHero1,
    },
  ];
  const trackRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);

  const scrollToStep = (index) => {
    const clampedIndex = Math.max(0, Math.min(steps.length - 1, index));
    setActiveStep(clampedIndex);
    const track = trackRef.current;
    const panel = track?.children?.[clampedIndex];
    if (panel && panel.scrollIntoView) {
      panel.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }
  };

  return (
    <div className="admissions-carousel-shell">
      <div className="admissions-carousel-track" ref={trackRef}>
        {steps.map((step, index) => (
          <article
            key={step.title}
            className={`admissions-carousel-panel ${index === activeStep ? 'active' : ''}`}
            onMouseEnter={() => setActiveStep(index)}
            onFocus={() => setActiveStep(index)}
            onClick={() => scrollToStep(index)}
            tabIndex={0}
            role="button"
            aria-label={`Admissions step ${index + 1}: ${step.title}`}
          >
            <div
              className="admissions-carousel-image"
              style={{ backgroundImage: `url(${step.image})` }}
              aria-hidden="true"
            />
            <div className="admissions-carousel-overlay" aria-hidden="true" />
            <div className="admissions-carousel-content">
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </div>
          </article>
        ))}
      </div>
      <div className="admissions-carousel-nav">
        <button
          type="button"
          className={`admissions-carousel-arrow left ${activeStep === 0 ? 'is-disabled' : ''}`}
          onClick={() => scrollToStep(activeStep - 1)}
          aria-label="Previous step"
          disabled={activeStep === 0}
        >
          ←
        </button>
        <button
          type="button"
          className={`admissions-carousel-arrow right ${activeStep === steps.length - 1 ? 'is-disabled' : ''}`}
          onClick={() => scrollToStep(activeStep + 1)}
          aria-label="Next step"
          disabled={activeStep === steps.length - 1}
        >
          →
        </button>
      </div>
    </div>
  );
}

function AdmissionsRequirementsSection() {
  const [activeTab, setActiveTab] = useState(0);

  const requirementsByLevel = [
    {
      level: 'Preschool & Kindergarten',
      description: 'Document Required For Admission',
      documents: [
        'Completed Application Form',
        'Copy of Applicant\'s Birth Certificate',
        'Copy of Family Card',
        'Copy of Parent\'s Identification Card',
        'Photograph of Applicant, with red background, size 3 x 4 cm',
        'Signed Form of Parents Acknowledgement on School Regulation',
      ],
      eligibility: [
        'Minimum age requirement based on level',
        'Readiness observation and assessment',
        'Parent-child interaction session',
      ],
    },
    {
      level: 'Primary',
      description: 'Prospective students are required to complete the admission registration form and submit all necessary supporting documents for the preliminary eligibility screening process.',
      documents: [
        'Copy of birth certificate',
        'Copy of passport/ID + KK',
        'Copy of KITAS (for foreign nationals)',
        '2 recent passport photos (3×4 cm)',
        'Report cards (Grades 4–6)',
        'Vaccination record',
        'Health form/reports (if applicable)',
      ],
      eligibility: [
        'Academic assessment in English, Math, and reasoning',
        'Teacher observation and learning style evaluation',
        'Student interview to understand interests and goals',
      ],
    },
    {
      level: 'Secondary',
      description: 'Academic review and student interview to ensure alignment with learning goals, leadership, and character development.',
      documents: [
        'Copy of birth certificate',
        'Copy of passport/ID + KK',
        'Copy of KITAS (for foreign nationals)',
        '2 recent passport photos (3×4 cm)',
        'Report cards (Grades 7–9)',
        'Vaccination record',
        'Health form/reports (if applicable)',
        'Academic transcript and recommendations',
      ],
      eligibility: [
        'Comprehensive academic assessment and aptitude testing',
        'In-depth student interview on aspirations and values',
        'Review of academic transcript and recommendations',
        'Leadership and character fit evaluation',
      ],
    },
  ];

  return (
    <div className="admissions-requirements-container">
      <div className="admissions-requirements-tabs">
        {requirementsByLevel.map((item, index) => (
          <button
            key={item.level}
            className={`admissions-requirements-tab ${index === activeTab ? 'active' : ''}`}
            onClick={() => setActiveTab(index)}
          >
            <span className="tab-label">{item.level}</span>
          </button>
        ))}
      </div>

      <div className="admissions-requirements-content adm-animate">
        <div className="requirements-card">
          <div className="requirements-header">
            <h4>{requirementsByLevel[activeTab].level}</h4>
            <p className="requirements-description">{requirementsByLevel[activeTab].description}</p>
          </div>

          <div className="requirements-grid">
            <div className="requirements-section">
              <h5>Required Documents</h5>
              <ul className="requirements-list">
                {requirementsByLevel[activeTab].documents.map((doc, idx) => (
                  <li key={idx} className="requirement-item">
                    <span className="requirement-icon">✓</span>
                    <span>{doc}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="requirements-section">
              <h5>Eligibility & Assessment</h5>
              <ul className="requirements-list">
                {requirementsByLevel[activeTab].eligibility.map((item, idx) => (
                  <li key={idx} className="requirement-item">
                    <span className="requirement-icon">→</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="requirements-note">
            <p>
              <strong>Need help?</strong> Contact our admissions team for clarification on any requirements or to schedule a consultation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdmissionsKeyDatesTimeline({ keyDates }) {
  // Fungsi untuk menentukan status berdasarkan bulan saat ini
  const getCardStatus = (tag) => {
    const currentMonth = new Date().getMonth() + 1; // 1-12
    const monthMap = {
      'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 
      'May': 5, 'Jun': 6, 'Jul': 7, 'Aug': 8,
      'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
    };
    
    const cardMonth = monthMap[tag];
    
    if (currentMonth === cardMonth) {
      return 'active'; // Sedang berlangsung
    } else if (currentMonth > cardMonth) {
      return 'completed'; // Sudah lewat
    } else {
      return 'upcoming'; // Akan datang
    }
  };

  return (
    <section className="admissions-key-dates" id="admissions-key-dates">
      <div className="container">
        <div className="key-dates-header">
          <span className="section-label">Timeline</span>
          <h2>Important Dates & Deadlines</h2>
          <p>Mark your calendar with key admissions dates to stay on track throughout the enrollment process.</p>
        </div>

        <div className="key-dates-grid">
          {keyDates.map((item, index) => {
            const status = getCardStatus(item.tag);
            return (
              <div key={item.title} className={`key-date-card ${status}`}>
                <div className="card-header">
                  <div className="card-number">{index + 1}</div>
                  <div className="card-month">{item.tag}</div>
                </div>
                <div className="card-body">
                  <div className="card-status-badge">
                    {status === 'active' && 'Active Now'}
                    {status === 'completed' && 'Completed'}
                    {status === 'upcoming' && 'Upcoming'}
                  </div>
                  <h3>{item.title}</h3>
                  <p className="card-date">{item.date}</p>
                  <p className="card-description">{item.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default AdmissionsPage;
